import User from "~/repository/models/User";
import type {
    ForgotCredentials,
    LoginCredentials,
    RegisterCredentials,
    ResetPasswordCredentials,
    SocialAuthRequestBody
} from "~/repository/modules/auth";
import type {AuthenticatedUser} from "~/repository/types";

type LogoutOptions = {
    withoutRequest: Boolean
}


export const useAuthStore = defineStore('auth', () => {
    const {$api} = useNuxtApp();
    const token = useCookie('auth_token');
    const user = ref<AuthenticatedUser | null | undefined>(undefined);
    const isLoggedIn = computed(() => !!user.value);
    const isBanned = ref(false);
    const banMessage = ref<string | null>(null);
    const banReason = ref<string | null>(null);
    const banReasonDescription = ref<string | null>(null);
    const banExpiry = ref<string | null>(null);
    const header = computed(() => `Bearer ${token.value}`);

    async function refresh() {
        try {
            // Return earlier if there is no token in cookies
            if (!token.value) return;

            user.value = await $api.auth.me();
            // @todo Refactor to 1 object instead of many vars (memory reduce)
            isBanned.value = false;
            banReason.value = null;
            banMessage.value = null;
            banReasonDescription.value = null;
            banExpiry.value = null;
        } catch (error: any) {
            //If `/me` returns 401 status code - forget about token, its invalid
            if (error?.response?.status === 401) {
                token.value = undefined
            }

            if ([401, 419].includes(error?.response?.status)) {
                const data = error.response?._data?.data;
                if (data && error.response?._data.message === 'You are banned.') {
                    isBanned.value = true;
                    banMessage.value = error.response?._data?.message || null;
                    banReason.value = data.reason || null;
                    banReasonDescription.value = data.reason_description || null;
                    banExpiry.value = data.expires_at || null;

                    user.value = data.user ? data.user : null;
                } else {
                    user.value = null;
                }
            } else {
                user.value = null;
            }
        }
    }

    async function login(credentials: LoginCredentials) {
        if (isLoggedIn.value) return;

        const data = await $api.auth.login(credentials);
        token.value = data.token;
        user.value = data.user;
        await nextTick(async () => {
            window.location.href = '/';
        });
        return true;
    }

    async function signup(credentials: RegisterCredentials) {
        if (isLoggedIn.value) return;

        const data = await $api.auth.signup(credentials);
        token.value = data.token;
        user.value = data.user;
        await nextTick(async () => {
            window.location.href = '/onboarding'
        });
        return true;
    }

    async function logout(opts: LogoutOptions) {
        if (!isLoggedIn.value) return;

        if (!opts.withoutRequest) {
            await $api.auth.logout();
        }
        user.value = null;
        isBanned.value = false;
        banReason.value = null;
        banReasonDescription.value = null;
        banExpiry.value = null;
        await nextTick(async () => {
            await navigateTo({name: 'login'});

            token.value = null;
            const {$notificationsChannel} = useNuxtApp();
            $notificationsChannel.unsubscribe();
        });
        return true;
    }

    async function forgotPassword(credentials: ForgotCredentials) {
        if (isLoggedIn.value) return;

        return await $api.auth.forgot(credentials);
    }

    async function resetPassword(credentials: ResetPasswordCredentials) {
        if (isLoggedIn.value) return;

        return await $api.auth.reset(credentials);
    }

    async function getSocialRedirectUrl(provider: string, redirectUrl: string): Promise<{ url: string }> {
        return await $api.auth.getSocialRedirectUrl(provider, redirectUrl);
    }

    async function authUsingSocialProvider(provider: string, credentials: SocialAuthRequestBody) {
        if (isLoggedIn.value) return;

        const data = await $api.auth.authUsingSocialProvider(provider, credentials);
        token.value = data.token;
        user.value = data.user;
        await nextTick(async () => {
            window.location.href = '/';
        });
        return true;
    }

    return {
        user,
        header,
        isLoggedIn,
        isBanned,
        banMessage,
        banReason,
        banReasonDescription,
        banExpiry,
        login,
        signup,
        logout,
        refresh,
        forgotPassword,
        resetPassword,
        getSocialRedirectUrl,
        authUsingSocialProvider
    };
});
